/* eslint-disable no-undefined,max-len,max-lines */
import {
  Component, EventEmitter, Input, OnInit, Output, SimpleChanges, inject,
} from '@angular/core';
import { IonDatetime } from '@ionic/angular';
import { LanguageService } from 'src/app/@core/language.service';
import { PwaService } from 'src/app/@core/pwa.service';
import { TranslateService } from '@ngx-translate/core';
import { dateIsValid, getIsoDateFromTimeValue, isTimeFormat } from 'src/app/@shared/app-helper';
import {
  format,
  parseISO,
  roundToNearestMinutes,
  setDefaultOptions,
} from 'date-fns';
import { isValidDate } from 'rxjs/internal/util/isDate';

// ### supported ISO 8601 formats for date values including min and max: ###
//
// Hour and Minute HH:mm
// Complete Date   YYYY-MM-DD
// Date and Time   YYYY-MM-DDTHH:mm
// UTC Timezone    YYYY-MM-DDTHH:mm:ssZ
//
// if providing utc timezone, date picker will parse it as datetime-local for presentation,
// but outputs applied date in utc timezone format

export type DatePickerPresentation = 'time' | 'date' | 'datetime-local';
export type IonatetimePresentation = 'time' | 'date' | 'date-time';

export type DatePickerConfig = {
  presentation: DatePickerPresentation;
  format?: string; // date-fns format (if not set, it uses default format based on locale)
  min?: string;
  max?: string;
  hasEmptyState?: boolean; // if not true, Validators.required is obsolete because we always have a value
};

export type IonDatetimeConfig = {
  presentation: IonatetimePresentation;
  isDateEnabled: (dateString: string) => boolean;
  multipleDates: boolean;
  title: string;
  cancelText: string;
  doneText: string;
};

export const MULTIPLE_DATES_SEPARATOR = '|';

@Component({
  selector: 'date-picker',
  standalone: false,
  styleUrls: ['./date-picker.component.scss'],
  templateUrl: './date-picker.component.html',
})
export class DatePickerComponent implements OnInit {
  @Input() public id: string; // necessary when using multiple ionic date pickers
  @Input() public config: Partial<DatePickerConfig> = {};
  @Input() public ionDatetimeConfig: Partial<IonDatetimeConfig>;
  @Input() public language: string;

  // see "supported ISO 8601 formats"
  @Input() public min: string | undefined;
  @Input() public max: string | undefined;
  @Input() public value: string | undefined;

  // single date: "see supported ISO 8601 formats"
  // multiple dates: array of single date format -> join(MULTIPLE_DATES_SEPARATOR)
  @Output() dateApplied: EventEmitter<string> = new EventEmitter<string>();

  public clearButtonVisible: boolean;
  public normalizedValue: string | string[];
  public normalizedRange: {
    min: string | undefined;
    max: string | undefined;
  } = {
      max: undefined,
      min: undefined,
    };

  public formattedValues: {
    datetime: string;
    date: string;
    time: string;
  } = {
      date: '',
      datetime: '',
      time: '',
    };

  public formattedMultipleDates: string[] = []; // ion-datetime exclusive

  public ionicPresentationIsActive: boolean;
  public datePickerReady: boolean;

  private isUTC: boolean;

  private languageService = inject(LanguageService);
  private translate = inject(TranslateService);
  private pwaService = inject(PwaService);

  public ngOnChanges(changes: SimpleChanges): void {
    // we want to make sure init() has been finished first
    if (!this.datePickerReady) {
      return;
    }

    const { language, value, min, max } = changes;

    if (min && min.previousValue !== min.currentValue) {
      this.normalizedRange.min = this.getNormalizedValue(min.currentValue);
    }

    if (max && max.previousValue !== max.currentValue) {
      this.normalizedRange.max = this.getNormalizedValue(max.currentValue);
    }

    if (value && value.previousValue !== value.currentValue) {
      this.onDateChanged(value.currentValue);
    }

    if (language && language.previousValue !== language.currentValue) {
      this.setLocale(language.currentValue);
      const currentDateValue = this.config.hasEmptyState || this.ionDatetimeConfig?.multipleDates
        ? this.value
        : this.normalizedValue as string;

      this.onDateChanged(currentDateValue);
    }
  }

  public ngOnInit(): void {
    this.init();
  }

  // receives single date/time/datetime or multiple dates
  public onIonValueConfirmed(ev: CustomEvent<IonDatetime>): void {
    const result: string | string[] = ev.detail.value;

    if (!this.ionDatetimeConfig.multipleDates) {
      this.onDateApplied(result as string);

      return;
    }

    if (Array.isArray(result)) {
      const dateStrings = result.sort((a, b) => {
        return a < b
          ? -1
          : 1;
      });

      this.onDateApplied(dateStrings.join(MULTIPLE_DATES_SEPARATOR));
    } else {
      this.onDateApplied(result);
    }
  }

  public onNativePickerChanged(ev: Event): void {
    const input = ev.target as HTMLInputElement;

    if (input.value === this.normalizedValue) {
      return;
    }

    this.onDateApplied(input.value);
  }

  public removeDate(ev: Event): void {
    ev.stopPropagation();

    this.onDateApplied('');
  }

  // Add ionDatetime-valid values from datepicker config if not already set in ionDatetimeConfig
  private setIonDatetimeConfig(): void {
    this.ionDatetimeConfig ??= {};

    this.ionDatetimeConfig.presentation ??= this.config.presentation === 'datetime-local'
      ? 'date-time'
      : this.config.presentation;

    // set default labels
    this.ionDatetimeConfig.cancelText ??= this.translate.instant('BUTTON.CANCEL');
    this.ionDatetimeConfig.doneText ??= this.translate.instant('BUTTON.OK');
  }

  private getPresentationMode(): 'ionic' | 'native' {
    if (this.ionDatetimeConfig) {
      return 'ionic';
    }

    // this.pwaService.isDesktop() seems true for android devices?
    if (!this.pwaService.isDesktop() || this.pwaService.isAndroid()) {
      return 'native';
    }

    return 'ionic';
  }

  // YYYY-MM-DDTHH:mm:ssZ to YYYY-MM-DDTHH:mm
  private getLocalDateFromUTC(value: string): string {
    if (!value.endsWith('Z')) {
      return value;
    }

    // e.g. 2024-04-06T09:49:47.544Z will be 2024-04-06T09:49
    const offset = roundToNearestMinutes(new Date(value), {
      roundingMethod: 'floor',
    });

    return format(offset, 'yyyy-MM-dd\'T\'HH:mm');
  }

  private getUTCFromLocalDate(value: string, presentation: DatePickerPresentation): string {
    const dateValue = presentation === 'time'
      ? this.getNormalizedTimeValue(value, false)
      : value;

    return new Date(dateValue).toISOString();
  }

  // for date button labels and preselected picker value
  // see https://date-fns.org/v3.6.0/docs/format for valid dateFormat values
  private setFormattedDateValues(value: string, presentation: DatePickerPresentation, dateFormat: string): void {
    this.formattedValues = {
      date: '',
      datetime: '',
      time: '',
    };

    if (!value) {
      return;
    }

    try {
      const currentDate = parseISO(this.getLocalDateValue(value, presentation));

      if (presentation === 'datetime-local') {
        this.formattedValues.time = format(currentDate, this.getDefaultDateFormat('time'));
        this.formattedValues.date = format(currentDate, this.getDefaultDateFormat('date'));
        this.formattedValues.datetime = format(currentDate, dateFormat);
      } else {
        this.formattedValues[presentation] = format(currentDate, dateFormat);
      }
    } catch (error) {
      console.error(`failed to set formatted date: value = ${value}, presentation = ${presentation}, format = ${dateFormat} - ${error}`);
    }
  }

  // for native date picker we need to ensure, date value is in format YYYY-MM-DD (date) or HH:mm (time) or YYYY-MM-DDTHH:mm (datetime-local)
  // for ionic date picker we need to ensure, date value is in format YYYY-MM-DD (date) or YYYY-MM-DDTHH:mm (datetime-local AND time)
  // useTimeFormat is always false when function is called by getFormattedValue
  private getLocalDateValue(value: string, presentation: DatePickerPresentation, useTimeFormat?: boolean): string | undefined {
    // ionic date picker doesn't like empty strings
    if (!value) {
      return undefined;
    }

    let localDateValue: string;

    try {
      localDateValue = value.endsWith('Z')
        ? this.getLocalDateFromUTC(value)
        : value;

      switch (presentation) {
        case 'time':
          localDateValue = this.getNormalizedTimeValue(localDateValue, useTimeFormat);
          break;

        case 'date':
          localDateValue = format(new Date(localDateValue), 'yyyy-MM-dd');
          break;

        default:
          localDateValue = format(new Date(localDateValue), 'yyyy-MM-dd\'T\'HH:mm');
      }
    } catch (error) {
      localDateValue = undefined;
      console.error(`failed to get local ${presentation} for value ${value} - ${error}`);
    }

    return localDateValue;
  }

  // returns normalized time value
  // YYYY-MM-DDTHH:mm for !useTimeFormat (valid ionDatetime value),
  // HH:mm for useTimeFormat (valid native date picker value)
  // IMPORTANT: param `value` is already a local date value
  private getNormalizedTimeValue(value: string, useTimeFormat: boolean): string | undefined {
    if (!value) {
      return undefined;
    }

    const valueIsTimeFormat = isTimeFormat(value);

    if (useTimeFormat) {
      return valueIsTimeFormat
        ? value
        : format(new Date(value), 'HH:mm');
    }

    if (valueIsTimeFormat) {
      return getIsoDateFromTimeValue(value);
    }

    return format(new Date(value), 'yyyy-MM-dd\'T\'HH:mm');
  }

  // restore time value if necessary
  // this is the case when we use multiple date pickers for one form control
  // (e.g. datetime via date and time picker)
  private getDatetimeFromDate(value: string): string {
    let appliedValue = value;

    if (!isValidDate(new Date(this.value))) {
      return value;
    }

    const parsedTime = format(this.value, 'HH:mm');
    let timeArray: number[] = [];

    if (isTimeFormat(parsedTime)) {
      timeArray = parsedTime.split(':')
        .map((v) => { return Number(v); });
    }

    if (timeArray.length === 2) {
      let currentDate = new Date(appliedValue);

      currentDate.setHours(timeArray[0], timeArray[1]);
      currentDate = roundToNearestMinutes(currentDate, {
        roundingMethod: 'floor',
      });

      appliedValue = format(currentDate, 'yyyy-MM-dd\'T\'HH:mm');
    }

    return this.isUTC
      ? this.getUTCFromLocalDate(appliedValue, this.config.presentation)
      : appliedValue;
  }

  private onDateApplied(value: string): void {
    let parsedValue: string = value;

    if (value) {
      if (this.isUTC) {
        parsedValue = this.getUTCFromLocalDate(value, this.config.presentation);
      } else if (this.config.presentation === 'time') {
        // we need to make sure, native picker and ionic picker both output the same format (HH:mm)
        parsedValue = this.getNormalizedTimeValue(value, true);
      }

      // add initial time value to applied date
      if (this.config.presentation === 'date' && this.value?.includes('T')) {
        parsedValue = this.getDatetimeFromDate(parsedValue);
      }
    } else if (this.config.hasEmptyState) {
      this.formattedValues = {
        date: '',
        datetime: '',
        time: '',
      };
      this.formattedMultipleDates = [];
    }

    this.dateApplied.emit(parsedValue);
  }

  private getDefaultDateFormat(presentation: DatePickerPresentation): string {
    if (presentation === 'time') {
      return 'p'; // 12:00 AM
    }

    if (presentation === 'date') {
      return 'P'; // 04/29/1453
    }

    return 'Pp'; // 04/29/1453, 12:00 AM
  }

  private getNormalizedValue(value: string): string | undefined {
    if (!value) {
      return undefined;
    }

    return this.getLocalDateValue(value, this.config.presentation, !this.ionicPresentationIsActive);
  }

  private setMultipleDates(value: string): void {
    if (!value) {
      this.normalizedValue = this.getFallbackDate();
      this.formattedMultipleDates = [];

      return;
    }

    this.normalizedValue = value.split(MULTIPLE_DATES_SEPARATOR)
      .map((v) => { return this.getLocalDateValue(v, this.config.presentation); });

    this.formattedMultipleDates = this.normalizedValue.map((date) => {
      return format(date, this.config.format);
    });
  }

  // fallback date = current utc date if not out of bounds
  // if current date is out of bounds set date either to min or max date
  private getFallbackDate(): string {
    this.isUTC = true;

    let currentDate = new Date().toISOString();
    let { min } = this;
    let { max } = this;

    // !!! for comparing time values we have to use valid iso dates (YYYY-MM-DD) !!!
    if (this.config.presentation === 'time') {
      currentDate = this.getNormalizedTimeValue(new Date().toISOString(), false);
      min = this.getNormalizedTimeValue(this.min, false);
      max = this.getNormalizedTimeValue(this.max, false);
    }

    if (!dateIsValid(currentDate, min, 'afterEqual')) {
      return this.normalizedRange.min;
    }

    if (!dateIsValid(currentDate, max, 'beforeEqual')) {
      return this.normalizedRange.max;
    }

    return currentDate;
  }

  private initDateValues(): void {
    // make sure we also have valid min and max values if defined
    for (const key of ['min', 'max']) {
      this.normalizedRange[key] = this.getNormalizedValue(this[key]);
    }

    this.isUTC = this.value?.endsWith('Z');
    let initialValue = this.value || this.getFallbackDate();
    const isMultiple = this.ionDatetimeConfig?.multipleDates;

    // set normalized input value
    if (isMultiple) {
      this.normalizedValue = initialValue.split(MULTIPLE_DATES_SEPARATOR)
        .map((v) => { return this.getLocalDateValue(v, this.config.presentation); })
        .filter((localDate) => { return !!localDate; });
    } else {
      this.normalizedValue = this.getNormalizedValue(initialValue);
    }

    // set fallback date + normalized value for invalid (!) date input / invalid multiple dates
    if (!this.normalizedValue || !this.normalizedValue.length) {
      this.value = undefined;
      initialValue = this.getFallbackDate();
      this.normalizedValue = this.getLocalDateValue(initialValue, this.config.presentation, !this.ionicPresentationIsActive);
      console.warn(`set date fallback for datepicker ${this.id}`, initialValue);
    }

    this.clearButtonVisible = !!this.value && this.config.hasEmptyState;

    // set formatted dates
    // if hasEmptyState is set, we use `this.value`, which can be undefined
    // otherwise we use initialValue, which can be this.value OR current date
    if (isMultiple) {
      const multiplesDates = Array.isArray(this.normalizedValue)
        ? this.normalizedValue as string[]
        : [this.normalizedValue];

      this.formattedMultipleDates = this.value
        ? multiplesDates.map((date) => {
          return format(date, this.config.format);
        })
        : [];
    } else {
      this.setFormattedDateValues(this.config.hasEmptyState
        ? this.value
        : initialValue, this.config.presentation, this.config.format);
    }
  }

  private onDateChanged(value: string): void {
    this.clearButtonVisible = this.config.hasEmptyState && !!value;

    if (this.ionDatetimeConfig?.multipleDates) {
      this.setMultipleDates(value);
    } else {
      // always set a date for normalizedValue, because `undefined` is not valid for native date input value
      this.normalizedValue = this.getNormalizedValue(value || this.getFallbackDate());
      this.setFormattedDateValues(value, this.config.presentation, this.config.format);
    }
  }

  private setLocale(currentLocale: string): void {
    if (!currentLocale) {
      return;
    }

    const locale = this.languageService.getDateLocaleByLanguage(currentLocale);

    if (locale) {
      setDefaultOptions({
        locale,
      });
    }
  }

  private init(): void {
    // set locale based on user/navigator language if not already set
    if (!this.language) {
      this.language = this.languageService.getLanguage();
    }

    this.setLocale(this.language);

    this.config.format ??= this.getDefaultDateFormat(this.config.presentation);
    this.ionicPresentationIsActive = this.getPresentationMode() === 'ionic';

    if (this.ionicPresentationIsActive) {
      this.setIonDatetimeConfig();
    }

    this.initDateValues();

    requestAnimationFrame(() => {
      // update form data when value is undefined
      // (when no initial value has been provided or date format is invalid)
      if (!this.value) {
        this.onDateApplied(this.config.hasEmptyState
          ? ''
          : this.normalizedValue as string);
      }

      this.datePickerReady = true;
    });
  }
}
